/**
 * Settings Screen - History Management Actions
 *
 * Extracted from app/(tabs)/settings.tsx (Line Count Refactor 2025-11-21)
 *
 * Handles clearing analysis history from cloud storage (Supabase),
 * local storage (Zustand), and React Query caches with user confirmation.
 *
 * Phase: 1B - Production Code Refactoring
 * Refactored: 2025-11-21 (settings.tsx: 345 → 172 lines)
 */

import { QueryClient } from '@tanstack/react-query';
import { TFunc } from './types';
import { queryKeys } from '@dramadetector/api-client';
import type { ClearAnalysisResult } from '@dramadetector/shared/schemas';
import { captureEvent } from '../analytics';

/**
 * Async callback that invokes the `clear-history` CF Worker handler via the
 * validated `useClearHistory` mutation hook. The caller (settings.tsx)
 * obtains this callback from the hook and passes it in so historyActions
 * stays React-free while still routing through the DTO-validated path.
 */
export type ClearHistoryCallback = (input: { userId: string }) => Promise<ClearAnalysisResult>;

export interface HistoryActionsParams {
  user: { id: string } | null;
  stats?: { totalAnalyses?: number };
  alert: (title: string, message?: string, buttons?: any[], options?: any) => void;
  t: TFunc;
  tCommon: TFunc;
  queryClient: QueryClient;
  clearLocalHistory: () => void;
  /** Injected clear-history mutation — wraps the validated worker call. */
  clearHistory: ClearHistoryCallback;
}

/**
 * Handles history clearing with user confirmation.
 *
 * Workflow:
 * 1. Get item count from stats (not paginated)
 * 2. Show confirmation alert with count
 * 3. On confirm: Call Edge Function (deletes DB + invalidates Redis) + clear local storage + remove React Query caches
 * 4. Track analytics event
 * 5. Show success/error alert
 *
 * Cache Optimization (2025-01-24):
 * - Calls clear-history Edge Function for server-side deletion + Redis invalidation
 * - Uses removeQueries (not resetQueries) for immediate UI clear
 * - Ensures no stale data reappears from Redis cache
 *
 * @param params History action parameters
 */
export function handleClearHistory(params: HistoryActionsParams): void {
  const { user, stats, alert, t, tCommon, queryClient, clearLocalHistory, clearHistory } = params;

  // Use stats to get actual total count (not paginated)
  const itemCount = stats?.totalAnalyses || 0;

  if (itemCount === 0) {
    alert(t('history.confirm_title'), t('history.no_history'));
    return;
  }

  alert(
    t('history.confirm_title'),
    t('history.confirm_message', { count: itemCount }),
    [
      { text: tCommon('cancel'), style: 'cancel' },
      {
        text: t('history.clear'),
        style: 'destructive',
        onPress: async () => {
          // Track clear history action
          captureEvent('feature_used', {
            feature: 'clear_history',
            screen: 'settings',
            items_cleared: itemCount,
            user_tenure_days: 0,
          });

          try {
            // 1. Clear cloud storage via validated CF Worker hook (for authenticated users)
            // The hook handles: DB deletion + Redis cache invalidation + DTO validation
            if (user) {
              try {
                const result = await clearHistory({ userId: user.id });
                console.log('[Clear History] Success:', result);
              } catch (err) {
                console.error('[Clear History] Worker call failed:', err);
                alert(tCommon('error'), t('history.error'));
                return;
              }
            }

            // 2. Clear local storage (Zustand store)
            clearLocalHistory();

            // 3. Remove React Query caches (both history and stats)
            // Use removeQueries with user-scoped key to match all analysis queries for this user
            // FIX (2025-12-09): queryKeys.analysis.all is a FUNCTION since security fix 2025-12-01
            // Must call it with user?.id to get the actual query key array
            queryClient.removeQueries({ queryKey: queryKeys.analysis.all(user?.id) });

            alert(tCommon('success'), t('history.success', { count: itemCount }));
          } catch (error) {
            console.error('[Clear History] Unexpected error:', error);
            alert(tCommon('error'), t('history.error'));
          }
        },
      },
    ]
  );
}
